import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  House, Ticket, ListChecks, ChatCircleDots, Megaphone, CheckSquare, ClipboardText,
  CalendarBlank, ChartBar, Buildings, Users, ChartPie, ShieldCheck, UserCircle, Bell,
  SignOut, List, Broom, UsersThree, Wallet, CalendarCheck, ChartBarHorizontal, FileText, Siren,
  Calculator as CalcIcon, Receipt,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { moduleEnabled } from "../lib/features";
import NotificationBell from "./NotificationBell";
import { CalculatorModal } from "./Calculator";
import ThemeToggle from "./ThemeToggle";
import LanguagePicker from "./LanguagePicker";

const STAFF = ["MANAGER", "CHAIRPERSON", "ADMIN"];

// Role-grouped navigation. Visibility is driven by the server feature-module config:
// each item carries a `moduleKey` and shows only when that module is enabled for the
// user's role. Items with no moduleKey are core (Home/Account/Notifications) or
// staff-only internal reports gated by `staff: true`.
const SECTIONS = [
  {
    items: [
      { to: "/", label: "Home", Icon: House },
      { to: "/visitors", label: "Visitors", Icon: UsersThree, moduleKey: "visitors" },
      { to: "/bookings", label: "Amenities", Icon: CalendarCheck, moduleKey: "bookings" },
      { to: "/complaints", label: "Tickets", Icon: Ticket, moduleKey: "complaints" },
      { to: "/tasks", label: "Tasks", Icon: ListChecks, moduleKey: "complaints", staff: true },
      { to: "/chat", label: "Chat", Icon: ChatCircleDots, moduleKey: "chat" },
    ],
  },
  {
    title: "Community",
    items: [
      { to: "/polls", label: "Polls", Icon: ChartBarHorizontal, moduleKey: "polls" },
      { to: "/documents", label: "Documents", Icon: FileText, moduleKey: "documents" },
      { to: "/sos", label: "SOS", Icon: Siren, moduleKey: "sos" },
    ],
  },
  {
    title: "Operations",
    items: [
      { to: "/announcements", label: "Announcements", Icon: Megaphone, moduleKey: "announcements", staff: true },
      { to: "/checklist", label: "Daily Checklist", Icon: CheckSquare, moduleKey: "checklist" },
      { to: "/staff", label: "Attendance", Icon: ClipboardText, moduleKey: "attendance" },
      { to: "/daily-report", label: "Daily Report", Icon: CalendarBlank, staff: true },
      { to: "/attendance-summary", label: "Attendance %", Icon: ChartBar, moduleKey: "attendance", staff: true },
      { to: "/cashbook", label: "Cash Book", Icon: Wallet, moduleKey: "cashbook" },
      { to: "/society-bills", label: "Society Bills", Icon: Receipt, staff: true },
    ],
  },
  {
    title: "Directory",
    items: [
      { to: "/flats", label: "Flat Directory", Icon: Buildings, moduleKey: "flats" },
      { to: "/tenants", label: "Tenants & Owners", Icon: Users, moduleKey: "tenants" },
      { to: "/oversight", label: "Oversight", Icon: ChartPie, moduleKey: "oversight" },
      { to: "/admin", label: "Users & Roles", Icon: ShieldCheck, roles: ["ADMIN"] },
      { to: "/admin/features", label: "Feature Modules", Icon: ChartPie, roles: ["ADMIN"] },
    ],
  },
  {
    title: "Masters",
    items: [
      { to: "/masters/staff/SECURITY", label: "Security Roster", Icon: ShieldCheck, moduleKey: "attendance" },
      { to: "/masters/staff/HOUSEKEEPING", label: "Housekeeping Roster", Icon: Broom, moduleKey: "attendance" },
      { to: "/masters/checklist", label: "Checklist Master", Icon: ListChecks, moduleKey: "checklist", staff: true },
    ],
  },
  {
    title: "You",
    items: [
      { to: "/notifications", label: "Notifications", Icon: Bell },
      { to: "/account", label: "Account", Icon: UserCircle },
    ],
  },
];

// Visibility: module-mapped items follow the server config for the role; a `staff: true`
// flag additionally restricts to office staff (for internal reports/editors); `roles`
// is an explicit allow-list for core admin routes. Core items (no key) always show.
function visibleItem(item, features, role, isStaff) {
  if (item.roles) return item.roles.includes(role);
  if (item.staff && !isStaff) return false;
  if (item.moduleKey) return moduleEnabled(features, item.moduleKey, role);
  return true;
}

export default function AppShell() {
  const { user, features, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const isStaff = STAFF.includes(user?.role);
  const role = user?.role;

  const sections = SECTIONS
    .map((s) => ({ ...s, items: s.items.filter((i) => visibleItem(i, features, role, isStaff)) }))
    .filter((s) => s.items.length);

  // Page title from the best-matching nav item.
  const allItems = sections.flatMap((s) => s.items);
  const current = allItems
    .filter((i) => (i.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(i.to)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  const title = current?.label || "Society Plus";
  const initial = (user?.name || "?").trim().charAt(0).toUpperCase();

  const Sidebar = (
    <aside className="flex h-full w-64 shrink-0 flex-col border-r border-[var(--line)] bg-[var(--shell)]">
      <NavLink to="/" end onClick={() => setDrawer(false)} aria-label="Go to home" data-testid="brand-home"
        className="flex items-center gap-3 px-5 py-5 transition hover:opacity-90 active:scale-[0.99]">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-neon-cyan text-black shadow-glow-cyan">
          <Buildings size={22} weight="fill" />
        </div>
        <div>
          <div className="text-[15px] font-extrabold leading-none text-[var(--fg)]">Society Plus</div>
          <div className="mt-1 text-[11px] font-semibold text-[var(--fg-dim)]">Silver Altire</div>
        </div>
      </NavLink>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {sections.map((s, si) => (
          <div key={si} className="mb-4">
            {s.title && (
              <div className="px-3 pb-1.5 pt-2 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[var(--fg-dim)]">{s.title}</div>
            )}
            <div className="space-y-0.5">
              {s.items.map(({ to, label, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  onClick={() => setDrawer(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13.5px] font-semibold transition ${
                      isActive
                        ? "bg-neon-cyan/15 text-neon-cyan"
                        : "text-[var(--fg-soft)] hover:bg-white/[0.04] hover:text-[var(--fg)]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon size={19} weight={isActive ? "fill" : "regular"} />
                      <span>{label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-[var(--line)] p-3">
        <div className="flex items-center gap-3 rounded-xl px-2 py-2">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-neon-cyan to-neon-lime text-[13px] font-extrabold text-black">{initial}</div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-bold text-[var(--fg)]">{user?.name || "—"}</div>
            <div className="truncate text-[11px] capitalize text-[var(--fg-dim)]">{(role || "").toLowerCase()}{user?.flat?.label ? ` · ${user.flat.label}` : ""}</div>
          </div>
          <button onClick={() => { logout(); navigate("/login"); }} title="Sign out"
            className="grid h-8 w-8 place-items-center rounded-lg text-[var(--fg-dim)] transition hover:bg-neon-pink/15 hover:text-neon-pink">
            <SignOut size={18} weight="bold" />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-aurora text-[var(--fg)]">
      <div className="hidden lg:block">{Sidebar}</div>

      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-[var(--line)] bg-[var(--shell)]/80 px-4 backdrop-blur md:px-6">
          <button className="grid h-10 w-10 place-items-center rounded-xl border border-[var(--line)] text-[var(--fg)] lg:hidden" onClick={() => setDrawer(true)}>
            <List size={20} weight="bold" />
          </button>
          <h1 className="text-lg font-extrabold tracking-tight text-[var(--fg)] md:text-xl">{title}</h1>
          <div className="flex-1" />
          <button onClick={() => setCalcOpen(true)} title="Calculator" data-testid="calc-open" aria-label="Open calculator"
            className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-neon-cyan to-neon-lime text-black shadow-[0_4px_14px_rgba(0,229,255,0.35)] transition active:scale-95">
            <CalcIcon size={18} weight="fill" />
          </button>
          <NotificationBell />
          <ThemeToggle />
          <LanguagePicker />
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-8 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Calculator overlay lives at the shell root (NOT inside the backdrop-blurred
          header) so its fixed positioning is relative to the viewport, centered. */}
      <AnimatePresence>{calcOpen && <CalculatorModal onClose={() => setCalcOpen(false)} />}</AnimatePresence>
    </div>
  );
}
